import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ModalDatePicker } from "src/components/molecules/ModalDatePicker";
import { ModalExercisePicker } from "src/components/molecules/ModalExercisePicker";
import * as S from "./Register.style";
import { PageContainer } from "src/components/organisms/contents";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { request } from "src/common/api";
import { BaseResponse } from "src/common/types/baseRespones";
import { GET_USER_INFO } from "src/common/querykeys";
import { useCodeLookup } from "src/common/hooks/useWorkCode";

const baseText = {
  fontWeight: 500,
  wordWrap: "break-word" as const,
};

const flexRow = {
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
};

const flexCol = {
  display: "flex",
  flexDirection: "column" as const,
};

export const inputBox = {
  background: "white",
  borderRadius: 8,
  outline: "1px var(--neutral-200, #E5E5E5) solid",
  outlineOffset: "-1px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
};

export const UserRegisterInfo = () => {
  const queryClient = useQueryClient();
  const { reg } = useParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [userId, setUserId] = useState<string>(""); // 아이디
  const [password, setPassword] = useState<string>(""); // 비밀번호
  const [userName, setUserName] = useState<string>(""); // 이름
  const [nickName, setNickName] = useState<string>(""); // 닉네임
  const [email, setEmail] = useState<string>(""); // 이메일
  const [gender, setGender] = useState<"M" | "F" | null>("M");
  const [birth, setBirth] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [workoutCount, setWorkoutCount] = useState<string>(""); // 운동 횟수
  const [open, setOpen] = useState<boolean>(false);
  const [showDateModal, setShowDateModal] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const navi = useNavigate();

  /* 유저 정보 추가하기 */
  const { mutateAsync: createdUserInfo } = useMutation({
    mutationFn: (inputs: any) => {
      return request<BaseResponse>({
        method: "POST",
        url: `/auth/register`,
        data: inputs,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.setQueryData([GET_USER_INFO], res.response);
      if (res.success) {
        navi("/register/success/join", { replace: true });
      }
    },
  });

  /* 유저 정보 수정하기 */
  const { mutateAsync: updateUserInfo } = useMutation({
    mutationFn: (inputs: any) => {
      return request<BaseResponse>({
        method: "PUT",
        url: `/users/me`,
        data: inputs,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.setQueryData([GET_USER_INFO], res.response);
      if (res.success) {
        navi("/register/success", { replace: true });
      }
    },
  });

  /* 운동 횟수 vLookup */
  const { data: workOutCode } = useCodeLookup();

  const formatBirth = (y: number, m: number, d: number) =>
    `${y}.${String(m).padStart(2, "0")}.${String(d).padStart(2, "0")}`;
  //   const formatBirth = (y: string, m: string, d: string) =>
  //     `${y}.${String(m).padStart(2, "0")}.${String(d).padStart(2, "0")}`;

  /* value => label  매핑 */
  const getValueByLabel = (value: string) =>
    workOutCode?.find((d: any) => d.value === value)?.label ?? "";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isAllFilled =
    reg === "join"
      ? !!userId &&
        !!password &&
        !!userName &&
        !!email &&
        !!gender &&
        !!birth &&
        !!height &&
        !!weight &&
        !!workoutCount
      : !!gender && !!birth && !!height && !!weight && !!workoutCount;

  /* 생년월일 나누기 */
  const [birthyear, birthday] = birth
    ? [birth.split(".")[0], birth.split(".").slice(1).join(".")]
    : ["", ""];

  const data = isAllFilled
    ? reg === "join"
      ? [
          {
            userId,
            password,
            userName,
            nickName,
            email,
            gender,
            birthyear,
            birthday,
            height,
            weight,
            workoutCount,
          },
        ]
      : [
          {
            gender,
            birthyear,
            birthday,
            height,
            weight,
            workoutCount,
          },
        ]
    : [];

  const handleSave = async (userInfo: any) => {
    const newErrors: Record<string, string> = {};

    if (reg) {
      // 회원가입 시 유효성 검사
      if (!/^[A-Za-z0-9]{6,12}$/.test(userId))
        newErrors.userId = "아이디는 영문과 숫자 조합 6~12자로 입력해주세요.";

      if (password.length < 4)
        newErrors.password = "비밀번호는 최소 4자 이상 입력해주세요.";

      if (!/^[가-힣]{2,4}$/.test(userName))
        newErrors.userName = "이름은 한글 2~4자로 입력해주세요.";

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        newErrors.email = "올바른 이메일 형식이 아닙니다.";

      // 오류가 있으면 상태로 저장 후 중단
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // 에러 없으면 초기화
      setErrors({});

      // console.log("userInfo  @@@@@@@@:", userInfo);
      try {
        await createdUserInfo(userInfo);
      } catch (error) {
        return;
      }
    } else {
      updateUserInfo(userInfo);
    }
  };
  return (
    <PageContainer isNavBar={false} active={null}>
      <>
        {/* Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "rgba(247,247,247,0.92)",
            backdropFilter: "blur(3px)",
            padding: `16px 0`,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <button
            aria-label="back"
            onClick={() => navi(-1)}
            style={{
              background: "transparent",
              border: "none",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            <svg width={22} height={22} viewBox="0 0 24 24">
              <path
                d="M19 12H5M5 12L12 5M5 12L12 19"
                stroke="#1F2937"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            {reg === "join" ? "회원가입" : "정보입력"}
          </div>
          <div style={{ width: 22, height: 22 }} />
        </div>
        {/* 하단 버튼 */}
        <div
          style={{
            width: "100%",
            maxWidth: "45rem",
            padding: isMobile ? 16 : 16,
            bottom: 0,
            position: "fixed",
            background: "white",
            borderTop: "1px rgba(0, 0, 0, 0.05) solid",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            boxSizing: "border-box",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 99,
          }}
        >
          <button
            style={{
              alignSelf: "stretch",
              height: isMobile ? 44 : 48,
              background: isAllFilled ? "#F57C00" : "#FFD9B3",
              borderRadius: 8,
              border: "none",
              cursor: isAllFilled ? "pointer" : "",
              fontSize: isMobile ? 14 : 16,
              ...baseText,
              color: "white",
            }}
            onClick={() => {
              if (isAllFilled) {
                handleSave(data[0]);
              }
            }}
          >
            다음
          </button>
        </div>
        {/* 메인 콘텐츠 */}
        <div
          style={{
            width: "100%",
            paddingTop: "16px",
            paddingBottom: isMobile ? 80 : 100,
            ...flexCol,
            gap: 24,
          }}
        >
          {/* 제목 */}
          <div style={{ width: 328, ...flexCol, gap: 8 }}>
            <div style={{ ...flexCol, alignItems: "left" }}>
              <div
                style={{
                  ...baseText,
                  fontSize: 18,
                  lineHeight: "28px",
                  color: "#737373",
                }}
              >
                2/2
              </div>
            </div>
            <div style={{ ...flexCol, gap: 2 }}>
              <div>
                <div
                  style={{
                    ...baseText,
                    fontSize: 20,
                    lineHeight: "28px",
                    color: "#262626",
                  }}
                >
                  기본 정보 입력
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    ...baseText,
                    fontSize: 16,
                    lineHeight: "24px",
                    color: "#525252",
                  }}
                >
                  건강 정보를 통해 권장 칼로리를 알려드릴게요.
                </div>
              </div>
            </div>
          </div>
          {reg && (
            <>
              {/* 아이디 */}
              <div style={{ ...flexRow, justifyContent: "space-between" }}>
                <label style={{ ...flexRow, gap: 4 }}>
                  <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                    아이디
                  </span>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      background: "#F57C00",
                      borderRadius: "50%",
                    }}
                  />
                </label>
                <div
                  style={{
                    width: 180,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      ...inputBox,
                      width: 180,
                      height: 48,
                      padding: "0 16px",
                      outline: errors.userName
                        ? "1px var(--neutral-200, #ff9797) solid"
                        : "1px var(--neutral-200, #E5E5E5) solid",
                    }}
                  >
                    <S.InputNum
                      type="text"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      placeholder="6~12자 이상 (영문, 숫자)"
                    />
                  </div>
                  {errors.userId && (
                    <span
                      style={{
                        marginTop: 4,
                        color: "#ff4545",
                        fontWeight: 500,
                      }}
                    >
                      {errors.userId}
                    </span>
                  )}
                </div>
              </div>
              {/* 비밀번호 */}
              <div style={{ ...flexRow, justifyContent: "space-between" }}>
                <label style={{ ...flexRow, gap: 4 }}>
                  <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                    비밀번호
                  </span>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      background: "#F57C00",
                      borderRadius: "50%",
                    }}
                  />
                </label>
                <div
                  style={{
                    width: 180,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      ...inputBox,
                      width: 180,
                      height: 48,
                      padding: "0 16px",
                      outline: errors.password
                        ? "1px var(--neutral-200, #ff9797) solid"
                        : "1px var(--neutral-200, #E5E5E5) solid",
                    }}
                  >
                    <S.InputNum
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="최소 4자 이상"
                    />
                  </div>
                  {errors.password && (
                    <span
                      style={{
                        marginTop: 4,
                        color: "#ff4545",
                        fontWeight: 500,
                      }}
                    >
                      {errors.password}
                    </span>
                  )}
                </div>
              </div>
              {/* 이름 */}
              <div style={{ ...flexRow, justifyContent: "space-between" }}>
                <label style={{ ...flexRow, gap: 4 }}>
                  <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                    이름
                  </span>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      background: "#F57C00",
                      borderRadius: "50%",
                    }}
                  />
                </label>
                <div
                  style={{
                    width: 180,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      ...inputBox,
                      width: 180,
                      height: 48,
                      padding: "0 16px",
                      outline: errors.userName
                        ? "1px var(--neutral-200, #ff9797) solid"
                        : "1px var(--neutral-200, #E5E5E5) solid",
                    }}
                  >
                    <S.InputNum
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="한글 이름 2 ~ 4자"
                    />
                  </div>
                  {errors.userName && (
                    <span
                      style={{
                        marginTop: 4,
                        color: "#ff4545",
                        fontWeight: 500,
                      }}
                    >
                      {errors.userName}
                    </span>
                  )}
                </div>
              </div>
              {/* 닉네임 */}
              <div style={{ ...flexRow, justifyContent: "space-between" }}>
                <label style={{ ...flexRow, gap: 4 }}>
                  <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                    닉네임
                  </span>
                  {/* <span
                    style={{
                      width: 6,
                      height: 6,
                      background: "#F57C00",
                      borderRadius: "50%",
                    }}
                  /> */}
                </label>
                <div
                  style={{
                    ...inputBox,
                    width: 180,
                    height: 48,
                    padding: "0 16px",
                  }}
                >
                  <S.InputNum
                    type="text"
                    value={nickName}
                    onChange={(e) => setNickName(e.target.value)}
                    placeholder="닉네임을 입력해주세요."
                  />
                </div>
              </div>
              {/* 이메일 */}
              <div style={{ ...flexRow, justifyContent: "space-between" }}>
                <label style={{ ...flexRow, gap: 4 }}>
                  <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                    이메일
                  </span>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      background: "#F57C00",
                      borderRadius: "50%",
                    }}
                  />
                </label>
                <div
                  style={{
                    width: 180,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      ...inputBox,
                      width: 180,
                      height: 48,
                      padding: "0 16px",
                      outline: errors.email
                        ? "1px var(--neutral-200, #ff9797) solid"
                        : "1px var(--neutral-200, #E5E5E5) solid",
                    }}
                  >
                    <S.InputNum
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="이메일을 입력해주세요."
                    />
                  </div>
                  {errors.email && (
                    <span
                      style={{
                        marginTop: 4,
                        color: "#ff4545",
                        fontWeight: 500,
                      }}
                    >
                      {errors.email}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* 성별 */}
          <div style={{ ...flexRow, justifyContent: "space-between" }}>
            <label style={{ ...flexRow, gap: 4 }}>
              <span style={{ ...baseText, fontSize: 16, color: "#525252" }}>
                성별
              </span>
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "#F57C00",
                  borderRadius: "50%",
                }}
              />
            </label>
            <div style={{ display: "flex", gap: 8, width: 180 }}>
              {[
                { key: "M", label: "남" },
                { key: "F", label: "여" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setGender(opt.key as "M" | "F")}
                  style={{
                    flex: 1,
                    height: 48,
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background:
                      gender === opt.key ? "#F57C00" : "var(--white, white)",
                    color: gender === opt.key ? "white" : "#525252",
                    ...baseText,
                    fontSize: 16,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 생년월일 */}
          <div style={{ ...flexRow, justifyContent: "space-between" }}>
            <label style={{ ...flexRow, gap: 4 }}>
              <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                생년월일
              </span>
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "#F57C00",
                  borderRadius: "50%",
                }}
              />
            </label>

            {/* 클릭하면 모달 열기 */}
            <div
              style={{
                ...inputBox,
                width: 180,
                height: 48,
                padding: "0 16px",
                cursor: "pointer",
                position: "relative",
              }}
              onClick={() => setShowDateModal(true)}
            >
              <span
                style={{ flex: 1, fontSize: 14, ...baseText, color: "#525252" }}
              >
                {birth !== "" ? (
                  birth
                ) : (
                  <span style={{ color: "#D4D4D4" }}>yyyy.mm.dd</span>
                )}
                <svg
                  width="18px"
                  height="18px"
                  viewBox="0 0 24 24"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </span>
            </div>

            {/* 모달 */}
            {showDateModal && (
              <ModalDatePicker
                data={birth}
                title="생년월일"
                onClose={() => setShowDateModal(false)}
                onConfirm={(year, month, day) => {
                  setBirth(formatBirth(year, month, day));
                  setShowDateModal(false);
                }}
              />
            )}
          </div>

          {/* 키 */}
          <div style={{ ...flexRow, justifyContent: "space-between" }}>
            <label style={{ ...flexRow, gap: 4 }}>
              <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                키
              </span>
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "#F57C00",
                  borderRadius: "50%",
                }}
              />
            </label>
            <div
              style={{ ...inputBox, width: 180, height: 48, padding: "0 16px" }}
            >
              <S.InputNum
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="예) 169"
              />
              <span
                style={{
                  ...baseText,
                  fontSize: 14,
                  color: "#525252",
                }}
              >
                cm
              </span>
            </div>
          </div>

          {/* 몸무게 */}
          <div style={{ ...flexRow, justifyContent: "space-between" }}>
            <label style={{ ...flexRow, gap: 4 }}>
              <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                몸무게
              </span>
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "#F57C00",
                  borderRadius: "50%",
                }}
              />
            </label>
            <div
              style={{ ...inputBox, width: 180, height: 48, padding: "0 16px" }}
            >
              <S.InputNum
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="예) 50.9"
              />
              <span style={{ ...baseText, fontSize: 14, color: "#525252" }}>
                kg
              </span>
            </div>
          </div>

          {/* 운동 횟수 */}
          <div style={{ ...flexRow, justifyContent: "space-between" }}>
            <label style={{ ...flexRow, gap: 4 }}>
              <span style={{ ...baseText, fontSize: 14, color: "#404040" }}>
                운동 횟수
              </span>
              <span
                style={{
                  width: 6,
                  height: 6,
                  background: "#F57C00",
                  borderRadius: "50%",
                }}
              />
            </label>
            <div
              style={{
                position: "relative",
                width: 180,
                height: 48,
              }}
            >
              <div
                style={{
                  ...inputBox,
                  justifyContent: "space-between",
                  cursor: "pointer",
                  height: "100%",
                  padding: "0 16px",
                  fontSize: 14,
                }}
                onClick={() => setOpen(true)}
              >
                <span style={{ color: workoutCount ? "" : "#D4D4D4" }}>
                  {getValueByLabel(workoutCount) || "선택"}
                </span>
                <span>▾</span>
              </div>
              {open && (
                <ModalExercisePicker
                  title="운동 횟수"
                  data={workOutCode ?? []}
                  onClose={() => setOpen(false)}
                  onConfirm={(exCount) => {
                    setWorkoutCount(exCount);
                    setOpen(false);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </>
    </PageContainer>
  );
};
